import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { GIFEncoder, quantize, applyPalette } from 'gifenc';
import { Loader2, Sparkles, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { findSticker, HEADER_STICKERS, stickerDataUrl, type StickerKind } from '../lib/headerStickers';
import { productService } from '../services/productService';
import { fieldClass, labelClass } from '../utils/form';

export type HeaderGifMotion = 'none' | 'textPulse' | 'gradientShift' | 'textSlide' | 'stickerBounce';

type PlacedSticker = {
  instanceId: string;
  kindId: string;
  x: number;
  y: number;
  scale: number;
  rotation: number;
};

type Props = {
  onGenerated: (url: string) => void;
};

/**
 * Wider than typical header strip aspect so `object-cover` never crops
 * top/bottom on desktop (that looked like a zoom).
 * ~1920×56 ≈ 34:1; a 1440×48 strip is ~30:1.
 */
const W = 1920;
const H = 56;
/** Keep under common nginx body limits. */
const MAX_UPLOAD_BYTES = 500 * 1024;
const STICKER_BASE = 28;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function clampByte(n: number) {
  return n < 0 ? 0 : n > 255 ? 255 : n;
}

function buildGradientAwarePalette(frameRgba: Uint8Array, from: string, to: string, maxColors = 256): number[][] {
  const [r1, g1, b1] = hexToRgb(from);
  const [r2, g2, b2] = hexToRgb(to);
  const swatchCount = 384;
  const merged = new Uint8Array(frameRgba.length + swatchCount * 4);
  merged.set(frameRgba);
  let o = frameRgba.length;
  for (let i = 0; i < swatchCount; i++) {
    const t = i / (swatchCount - 1);
    const shade = (i % 3) * 0.02;
    merged[o++] = clampByte(lerp(r1, r2, t) * (1 - shade) + 255 * shade * 0.15);
    merged[o++] = clampByte(lerp(g1, g2, t) * (1 - shade) + 255 * shade * 0.15);
    merged[o++] = clampByte(lerp(b1, b2, t) * (1 - shade) + 255 * shade * 0.1);
    merged[o++] = 255;
  }
  return quantize(merged, maxColors, { format: 'rgb565' });
}

function loadStickerImages(): Promise<Map<string, HTMLImageElement>> {
  return Promise.all(
    HEADER_STICKERS.map(
      (s) =>
        new Promise<[string, HTMLImageElement]>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve([s.id, img]);
          img.onerror = () => reject(new Error(s.id));
          img.src = stickerDataUrl(s);
        }),
    ),
  ).then((entries) => new Map(entries));
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  opts: {
    text: string;
    subtitle: string;
    from: string;
    to: string;
    motion: HeaderGifMotion;
    frame: number;
    total: number;
    stickers: PlacedSticker[];
    images: Map<string, HTMLImageElement>;
    selectedId?: string | null;
    showSelection?: boolean;
  },
) {
  const { text, subtitle, from, to, motion, frame, total, stickers, images, selectedId, showSelection } = opts;
  const phase = (Math.sin((frame / Math.max(1, total)) * Math.PI * 2) + 1) / 2;
  const t = total <= 1 ? 0 : frame / Math.max(1, total - 1);
  const [r1, g1, b1] = hexToRgb(from);
  const [r2, g2, b2] = hexToRgb(to);

  let shift = 0;
  if (motion === 'gradientShift') shift = t * 0.28;

  const grad = ctx.createLinearGradient(-shift * W, 0, W * (1.05 + shift), H * 1.15);
  const stops = 24;
  for (let i = 0; i <= stops; i++) {
    const p = i / stops;
    const e = p * p * (3 - 2 * p);
    grad.addColorStop(
      p,
      `rgb(${Math.round(lerp(r1, r2, e))},${Math.round(lerp(g1, g2, e))},${Math.round(lerp(b1, b2, e))})`,
    );
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.2, W / 2, H / 2, W * 0.65);
  vig.addColorStop(0, 'rgba(255,255,255,0.06)');
  vig.addColorStop(1, 'rgba(0,0,0,0.14)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  let alpha = 1;
  let yOffset = 0;
  let xOffset = 0;
  if (motion === 'textPulse') alpha = 0.9 + phase * 0.1;
  if (motion === 'textSlide') {
    xOffset = Math.sin((frame / Math.max(1, total)) * Math.PI * 2) * 24;
    yOffset = Math.sin((frame / Math.max(1, total)) * Math.PI * 2) * 1;
  }

  // Keep content inside a vertical safe band so cover-crop / rounding never clips glyphs.
  const padY = 10;
  const contentH = H - padY * 2;

  for (const s of stickers) {
    const img = images.get(s.kindId);
    if (!img) continue;
    const bounce =
      motion === 'stickerBounce'
        ? 1 + Math.sin((frame / Math.max(1, total)) * Math.PI * 2 + s.x * 0.08) * 0.1
        : 1;
    const size = Math.min(STICKER_BASE * s.scale * bounce, contentH * 0.9);
    const px = (s.x / 100) * W;
    const py = padY + (s.y / 100) * contentH;
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate((s.rotation * Math.PI) / 180);
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
    if (showSelection && selectedId === s.instanceId) {
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 4]);
      ctx.strokeRect(-size / 2 - 2, -size / 2 - 2, size + 4, size + 4);
      ctx.setLineDash([]);
    }
    ctx.restore();
  }

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.direction = 'rtl';
  ctx.fillStyle = '#ffffff';
  ctx.lineJoin = 'round';

  const hasSub = Boolean(subtitle.trim());
  const midY = padY + contentH / 2 + yOffset;
  const titleY = hasSub ? midY - 8 : midY;
  ctx.font = '700 22px Vazirmatn, Tahoma, "Segoe UI", sans-serif';
  const title = text.trim() || 'متن هدر';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(0,0,0,0.28)';
  ctx.strokeText(title, W / 2 + xOffset, titleY, W - 320);
  ctx.fillText(title, W / 2 + xOffset, titleY, W - 320);

  if (hasSub) {
    ctx.font = '500 14px Vazirmatn, Tahoma, "Segoe UI", sans-serif';
    ctx.globalAlpha = alpha * 0.94;
    ctx.lineWidth = 2;
    const sub = subtitle.trim();
    ctx.strokeText(sub, W / 2 + xOffset, midY + 10, W - 360);
    ctx.fillText(sub, W / 2 + xOffset, midY + 10, W - 360);
  }
  ctx.restore();
}

function hitTestSticker(
  stickers: PlacedSticker[],
  xPct: number,
  yPct: number,
): string | null {
  const padY = 10;
  const contentH = H - padY * 2;
  for (let i = stickers.length - 1; i >= 0; i--) {
    const s = stickers[i];
    const half = Math.min(STICKER_BASE * s.scale, contentH * 0.9) / 2;
    const halfX = (half / W) * 100;
    const halfY = (half / contentH) * 100;
    // yPct is relative to full canvas; stickers are placed in content band
    const sy = (padY / H) * 100 + (s.y * contentH) / H;
    if (Math.abs(xPct - s.x) <= halfX + 1.5 && Math.abs(yPct - sy) <= halfY + 2) {
      return s.instanceId;
    }
  }
  return null;
}

export function HeaderGifBuilder({ onGenerated }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // GIF copy is independent of the admin "title" field — never sync from form.
  const [text, setText] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [from, setFrom] = useState('#0f3d32');
  const [to, setTo] = useState('#c45c4a');
  const [motion, setMotion] = useState<HeaderGifMotion>('none');
  const [stickers, setStickers] = useState<PlacedSticker[]>([]);
  const [paletteKindId, setPaletteKindId] = useState(HEADER_STICKERS[0]?.id ?? 'arrow-bold');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [images, setImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [busy, setBusy] = useState(false);
  const previewFrame = useRef(0);

  const selected = useMemo(
    () => stickers.find((s) => s.instanceId === selectedId) ?? null,
    [stickers, selectedId],
  );

  useEffect(() => {
    let cancelled = false;
    void loadStickerImages()
      .then((map) => {
        if (!cancelled) setImages(map);
      })
      .catch(() => {
        if (!cancelled) toast.error('بارگذاری استیکرها ناموفق بود');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || images.size === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      if (now - last > 66) {
        last = now;
        if (motion !== 'none') previewFrame.current = (previewFrame.current + 1) % 36;
        else previewFrame.current = 0;
        drawFrame(ctx, {
          text,
          subtitle,
          from,
          to,
          motion,
          frame: previewFrame.current,
          total: 36,
          stickers,
          images,
          selectedId,
          showSelection: true,
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, subtitle, from, to, motion, stickers, images, selectedId]);

  const addSticker = useCallback(
    (kindId: string, x: number, y: number) => {
      if (stickers.length >= 12) {
        toast.error('حداکثر ۱۲ استیکر');
        return;
      }
      if (!findSticker(kindId)) return;
      const instanceId = `${kindId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setStickers((prev) => [
        ...prev,
        {
          instanceId,
          kindId,
          x: Math.min(96, Math.max(4, x)),
          y: Math.min(85, Math.max(15, y)),
          scale: 1,
          rotation: 0,
        },
      ]);
      setSelectedId(instanceId);
    },
    [stickers.length],
  );

  const updateSelected = (patch: Partial<PlacedSticker>) => {
    if (!selectedId) return;
    setStickers((prev) =>
      prev.map((s) => (s.instanceId === selectedId ? { ...s, ...patch } : s)),
    );
  };

  const removeStickerById = (id: string) => {
    setStickers((prev) => prev.filter((s) => s.instanceId !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
  };

  const onPreviewClick = (e: MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const hit = hitTestSticker(stickers, x, y);
    if (hit) {
      setSelectedId(hit);
      return;
    }
    addSticker(paletteKindId, x, y);
  };

  const generateAndUpload = async () => {
    if (!text.trim()) {
      toast.error('متن هدر را وارد کنید');
      return;
    }
    if (images.size === 0) {
      toast.error('استیکرها هنوز لود نشده‌اند؛ چند لحظه صبر کنید');
      return;
    }
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error('canvas');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const frameCount = motion === 'none' ? 1 : 8;
      const delay = motion === 'none' ? 400 : 110;
      const gif = GIFEncoder();

      const samples: Uint8Array[] = [];
      for (const f of [0, Math.floor(frameCount / 2), frameCount - 1]) {
        drawFrame(ctx, {
          text,
          subtitle,
          from,
          to,
          motion,
          frame: Math.min(f, frameCount - 1),
          total: frameCount,
          stickers,
          images,
          showSelection: false,
        });
        samples.push(new Uint8Array(ctx.getImageData(0, 0, W, H).data));
      }
      const mergedSample = new Uint8Array(samples.reduce((n, s) => n + s.length, 0));
      let off = 0;
      for (const s of samples) {
        mergedSample.set(s, off);
        off += s.length;
      }
      const sharedPalette = buildGradientAwarePalette(mergedSample, from, to, 64);

      for (let i = 0; i < frameCount; i++) {
        drawFrame(ctx, {
          text,
          subtitle,
          from,
          to,
          motion,
          frame: i,
          total: frameCount,
          stickers,
          images,
          showSelection: false,
        });
        const rgba = new Uint8Array(ctx.getImageData(0, 0, W, H).data);
        const index = applyPalette(rgba, sharedPalette, 'rgb565');
        gif.writeFrame(index, W, H, { palette: sharedPalette, delay, repeat: 0 });
      }
      gif.finish();
      const bytes = gif.bytes();
      const copy = new Uint8Array(bytes.byteLength);
      copy.set(bytes);
      const file = new File([new Blob([copy], { type: 'image/gif' })], `header-${Date.now()}.gif`, {
        type: 'image/gif',
      });
      if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error(
          `حجم GIF حدود ${(file.size / 1024).toFixed(0)}KB است؛ حرکت را «بدون حرکت» بگذارید`,
        );
      }
      const res = await productService.uploadFile(file);
      // Prefer same-origin /uploads path so storefront proxy works everywhere
      let url = res.url || '';
      try {
        const u = new URL(url);
        if (u.pathname.startsWith('/uploads/')) url = u.pathname;
      } catch {
        /* keep as-is */
      }
      onGenerated(url);
      toast.success(`GIF ساخته شد (${Math.round(file.size / 1024)}KB)`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ساخت GIF ناموفق بود');
    } finally {
      setBusy(false);
    }
  };

  const kindThumb = (kind: StickerKind) => (
    <img src={stickerDataUrl(kind)} alt={kind.label} className="h-8 w-8 object-contain" draggable={false} />
  );

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Sparkles className="h-4 w-4 text-indigo-600" />
        ساخت GIF هدر
      </div>
      <p className="text-xs leading-6 text-slate-500">
        استیکر گرافیکی انتخاب کنید، روی پیش‌نمایش کلیک کنید تا اضافه شود. برای تنظیم، همان استیکر را از لیست یا روی پیش‌نمایش انتخاب کنید.
      </p>

      <div className="grid gap-3 lg:grid-cols-[1.35fr_1fr]">
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-600">پیش‌نمایش نهایی</p>
          <button
            type="button"
            onClick={onPreviewClick}
            className="block w-full overflow-hidden rounded-lg border-2 border-dashed border-indigo-200 bg-slate-900 text-start"
          >
            <canvas ref={canvasRef} width={W} height={H} className="pointer-events-none h-11 w-full object-contain sm:h-12" />
          </button>
          <p className="text-[11px] text-slate-400">
            کلیک روی فضای خالی = افزودن · کلیک روی استیکر = انتخاب
          </p>
        </div>

        <div className="flex h-64 flex-col rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-2 flex shrink-0 items-center justify-between">
            <p className="text-xs font-semibold text-slate-700">پک استیکر</p>
            <span className="text-[11px] text-slate-400">{stickers.length}/12</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5">
              {HEADER_STICKERS.map((kind) => (
                <button
                  key={kind.id}
                  type="button"
                  title={kind.label}
                  onClick={() => setPaletteKindId(kind.id)}
                  onDoubleClick={() => addSticker(kind.id, stickers.length % 2 === 0 ? 10 : 90, 50)}
                  className={`flex flex-col items-center gap-0.5 rounded-lg border p-1.5 ${
                    paletteKindId === kind.id
                      ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300'
                      : 'border-slate-100 hover:border-indigo-200'
                  }`}
                >
                  {kindThumb(kind)}
                  <span className="max-w-full truncate text-[9px] text-slate-500">{kind.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {stickers.length > 0 && (
        <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold text-slate-700">استیکرهای روی هدر</p>
          <ul className="flex flex-wrap gap-2">
            {stickers.map((s, i) => {
              const kind = findSticker(s.kindId);
              const active = s.instanceId === selectedId;
              return (
                <li key={s.instanceId} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedId(s.instanceId)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs ${
                      active ? 'bg-indigo-100 ring-2 ring-indigo-500' : 'bg-slate-50 ring-1 ring-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {kind ? kindThumb(kind) : null}
                    <span className="font-medium text-slate-700">
                      {i + 1}. {kind?.label ?? s.kindId}
                    </span>
                  </button>
                  <button
                    type="button"
                    aria-label={`حذف ${kind?.label ?? 'استیکر'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeStickerById(s.instanceId);
                    }}
                    className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>

          {selected ? (
            <div className="grid gap-2 rounded-lg bg-indigo-50/80 p-3 sm:grid-cols-2">
              <p className="sm:col-span-2 text-[11px] font-semibold text-indigo-900">
                در حال تنظیم: {findSticker(selected.kindId)?.label}
                <button
                  type="button"
                  className="ms-2 text-indigo-600 underline"
                  onClick={() => setSelectedId(null)}
                >
                  بستن تنظیمات
                </button>
              </p>
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                افقی
                <input
                  type="range"
                  min={4}
                  max={96}
                  value={selected.x}
                  onChange={(e) => updateSelected({ x: Number(e.target.value) })}
                  className="flex-1"
                />
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                عمودی
                <input
                  type="range"
                  min={15}
                  max={85}
                  value={selected.y}
                  onChange={(e) => updateSelected({ y: Number(e.target.value) })}
                  className="flex-1"
                />
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                سایز
                <input
                  type="range"
                  min={50}
                  max={220}
                  value={Math.round(selected.scale * 100)}
                  onChange={(e) => updateSelected({ scale: Number(e.target.value) / 100 })}
                  className="flex-1"
                />
              </label>
              <label className="flex items-center gap-2 text-[11px] text-slate-600">
                چرخش
                <input
                  type="range"
                  min={-180}
                  max={180}
                  value={selected.rotation}
                  onChange={(e) => updateSelected({ rotation: Number(e.target.value) })}
                  className="flex-1"
                />
              </label>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={() => removeStickerById(selected.instanceId)}
                  className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1.5 text-[11px] font-medium text-red-600 ring-1 ring-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  حذف این استیکر
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400">برای تنظیم، یکی از استیکرهای بالا را انتخاب کنید.</p>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClass}>متن روی GIF</label>
          <input className={fieldClass} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>زیرمتن (اختیاری)</label>
          <input className={fieldClass} value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>رنگ شروع گرادیان</label>
          <input type="color" className="h-10 w-full cursor-pointer rounded-lg border border-slate-300" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className={labelClass}>رنگ پایان گرادیان</label>
          <input type="color" className="h-10 w-full cursor-pointer rounded-lg border border-slate-300" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelClass}>حرکت</label>
          <select className={fieldClass} value={motion} onChange={(e) => setMotion(e.target.value as HeaderGifMotion)}>
            <option value="none">بدون حرکت</option>
            <option value="textPulse">نبض متن</option>
            <option value="gradientShift">حرکت گرادیان</option>
            <option value="textSlide">لغزش متن</option>
            <option value="stickerBounce">پریدن استیکرها</option>
          </select>
        </div>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={() => void generateAndUpload()}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {busy ? 'در حال encode…' : 'ساخت GIF و قرار دادن روی هدر'}
      </button>
    </div>
  );
}
